import {
  SELF_HEALING_STALE_EPOCH_CODE,
  type BackupRecoveryRow,
  type NativeStateSnapshot,
  type RecordingLeaseParams,
  type StopReason,
  type TerminalOutcome,
} from '@/tauri/controller/self-healing-stop';
import { logger } from '@/utils/logger/logger';
import { bridgeErrorKind } from '@heidi/plugin-core';
import { sanitizeError } from '@heidi/runtime/logger';
import { type AvailableDevices } from 'src-tauri/src/bindings/AvailableDevices';
import { type RecordingDevices } from 'src-tauri/src/bindings/RecordingDevices';
import { type RecordingLiveness } from 'src-tauri/src/bindings/RecordingLiveness';
import { type RemoveFileResult } from 'src-tauri/src/bindings/RemoveFileResult';
import { type RemoveOutcome } from 'src-tauri/src/bindings/RemoveOutcome';
import { type WavWriterFailurePayload } from '../events/wav-writer-health';
import { ipc_invoke } from '../ipc';
import { isTauri } from '../utils';

const RECORDING_START_TIMEOUT_ERROR_CODE = 'RECORDING_START_TIMEOUT';
// Already shown as a re-grant toast off the "ERROR" event (see ipc.ts). Suppressed here so
// start_recording's rejection does not add a second raw-message toast.
const AUDIO_SCK_PERMISSION_DENIED_ERROR_CODE = 'AUDIO_SCK_PERMISSION_DENIED';

// Every backup extraction failure. The generic toast would only leak the raw Rust
// message, so callers show their own prompt or a localized toast instead.
const BACKUP_EXTRACTION_FAILED_ERROR_CODE = 'RECORDING_BACKUP_EXTRACTION_FAILED';

// Rust refused a path or session id outside the audio directories. That is a caller bug, never
// something the clinician can act on, so the generic toast is suppressed and the rejection stays loud.
const AUDIO_PATH_OUTSIDE_MANAGED_DIR_ERROR_CODE = 'AUDIO_PATH_OUTSIDE_MANAGED_DIR';
const AUDIO_SESSION_ID_INVALID_ERROR_CODE = 'AUDIO_SESSION_ID_INVALID';

// The defensive double-stop already treats this as a no-op (isRecorderAlreadyStopped),
// so suppressed only here; a normal stop still shows the generic toast.
const RECORDING_NOT_STARTED_ERROR_CODE = 'RECORDING_NOT_STARTED';
// Relocated to `@heidi/plugin-core` as `IpcResult`; re-exported under the old name for
// existing consumers.
export { type IpcResult as ICPResponseData } from '@heidi/plugin-core';

export type RecordingState = 'Recording' | 'Paused' | 'Stopped';

/** What `remove_recording_file` did, or `refused` when Rust rejected the path. */
export type RemoveFileOutcome = RemoveOutcome | 'refused';

/**
 * Microphone permission status from the OS; mirrors Rust `MicPermissionResult` (pinned by a serde test).
 * `NotDetermined` means never asked: only user-initiated flows may open the mic, which fires the
 * OS prompt. Background consumers treat it as not granted. Windows never reports it.
 */
export type MicPermissionStatus =
  | 'Authorized'
  | 'NotDetermined'
  | 'Denied'
  | 'Restricted'
  | 'Error';

/** Result of `check_input_device_health` (camelCase from Rust). */
export type InputDeviceHealth = {
  isSystemMuted: boolean;
  volumeScalar: number | null;
  /** `MUTED` | `ZERO_LEVEL` when unhealthy. */
  reason: string | null;
};

/**
 * Capture-start gates, locked in Rust for the lifetime of the capture. An options object so a
 * call site that misses a new gate is visible at review.
 */
export type RecordingStartOptions = {
  /**
   * When true, the Rust recorder creates PCM taps so the streaming
   * chunk-upload pipeline can feed FFmpeg directly. Driven by the
   * `release-apps-rust-chunk-upload-desktop` flag.
   */
  useChunkStreaming?: boolean;
  /** The `op-desktop-disable-elastic-pcm-tap` kill switch: true builds the 256-block taps and old pacing. */
  disableElasticPcmTap?: boolean;
  /** Lock the WAV-writer recovery gate for the lifetime of the native capture. */
  enableWavWriterRecovery?: boolean;
  /**
   * Raw Flagsmith payload of the recovery flag, decoded in Rust into the WAV-writer stall budget.
   * Missing or malformed keeps the 10s default; `wavWriterStallBudgetSecs: 0` restores fail-fast.
   */
  wavWriterRecoveryPayload?: Record<string, unknown>;
  /**
   * Arm the native low-disk-space monitor for this capture (30s poll of the
   * audio volume; warns via `RECORDING_DISK_SPACE_LOW`, never blocks). Driven
   * by the `release-apps-low-disk-warning-desktop` flag.
   */
  enableLowDiskMonitor?: boolean;
  /**
   * Remaining budget for native recorder startup. Rust owns this deadline so
   * producer handles are stopped and joined before a typed timeout is returned.
   */
  startTimeoutMs?: number;
  /**
   * True for the restart half of a mid-recording device change. On macOS with an unchanged output
   * device this skips the 1s Safe Start sleep. Windows keeps the full Mic -> 1s -> Speaker
   * sequence to guard its cpal speaker stream against the Bluetooth endpoint transition.
   */
  isMidRecordingRestart?: boolean;
};

/**
 * Typed error from the self-healing bridges, carrying the Rust `error_code`. These bridges
 * raise no destructive toast, so the caller owns error UX.
 */
export class SelfHealingBridgeError extends Error {
  readonly errorCode?: string;

  constructor(message: string, errorCode?: string) {
    super(message);
    this.name = 'SelfHealingBridgeError';
    this.errorCode = errorCode;
  }
}

/** Shape Rust returns via `Ok(IpcResponse::from(...))`. See `ipc/response.rs`. */
type IpcResponseEnvelope<D> = {
  result?: { data: D } | null;
  error?: { message: string; error_code?: string } | null;
};

/**
 * Raw IPC invoke for the self-healing bridges. Like `ipc_invoke` but throws a typed
 * {@link SelfHealingBridgeError} instead of showing a toast.
 */
async function invokeSelfHealing<T>(method: string, params?: object): Promise<T> {
  const { invoke } = await import('@tauri-apps/api/core');
  const response = await invoke<IpcResponseEnvelope<T>>(method, { params });
  if (response?.error != null) {
    throw new SelfHealingBridgeError(response.error.message, response.error.error_code);
  }
  if (response?.result == null) {
    throw new SelfHealingBridgeError(
      `self-healing bridge: ${method} returned a malformed IpcResponse`
    );
  }
  return response.result.data;
}

class RecordingModelController {
  async get_recording_device_names(): Promise<RecordingDevices> {
    return ipc_invoke(`get_recording_device_names`, {}).then(
      (res: { data: RecordingDevices }) => res.data
    );
  }

  async start(
    inputDevice: string,
    outputDevice: string,
    sessionId: string,
    options: RecordingStartOptions = {}
  ): Promise<string> {
    const {
      useChunkStreaming = false,
      disableElasticPcmTap = false,
      enableWavWriterRecovery = false,
      enableLowDiskMonitor = false,
      wavWriterRecoveryPayload,
      startTimeoutMs,
      isMidRecordingRestart = false,
    } = options;
    return ipc_invoke(
      `start_recording`,
      {
        recording_devices: {
          input_device_name: inputDevice,
          output_device_name: outputDevice,
        },
        session_id: sessionId,
        use_chunk_streaming: useChunkStreaming,
        enable_wav_writer_recovery: enableWavWriterRecovery,
        enable_low_disk_monitor: enableLowDiskMonitor,
        is_mid_recording_restart: isMidRecordingRestart,
        // Sent only when the kill switch is on: Rust defaults to the elastic tap.
        ...(disableElasticPcmTap ? { disable_elastic_pcm_tap: true } : {}),
        ...(wavWriterRecoveryPayload === undefined
          ? {}
          : { wav_writer_recovery_payload: wavWriterRecoveryPayload }),
        ...(startTimeoutMs === undefined ? {} : { start_timeout_ms: startTimeoutMs }),
      },
      {
        suppressErrorCodes: [
          RECORDING_START_TIMEOUT_ERROR_CODE,
          AUDIO_SCK_PERMISSION_DENIED_ERROR_CODE,
        ],
      }
    ).then((res: { data: string }) => res.data);
  }

  async pause(): Promise<null> {
    return ipc_invoke(`pause_recording`, {}).then((res: { data: null }) => res.data);
  }

  async resume(): Promise<null> {
    return ipc_invoke(`resume_recording`, {}).then((res: { data: null }) => res.data);
  }

  async stop(): Promise<string> {
    return ipc_invoke(`stop_recording`, {}).then((res: { data: string }) => res.data);
  }

  async stop_without_backup_file(): Promise<string> {
    return ipc_invoke(
      `stop_recording_without_backup_file`,
      {},
      { suppressErrorCodes: [RECORDING_NOT_STARTED_ERROR_CODE] }
    ).then((res: { data: string }) => res.data);
  }

  async get_backup_file(): Promise<string> {
    return ipc_invoke(
      `get_recording_backup_file`,
      {},
      { suppressErrorCodes: [BACKUP_EXTRACTION_FAILED_ERROR_CODE] }
    ).then((res: { data: string }) => res.data);
  }

  async get_raw_audio(sessionId: string): Promise<Uint8Array> {
    return ipc_invoke(`get_raw_audio`, { id: sessionId }).then(
      (res: { data: ArrayBuffer }) => {
        return new Uint8Array(res.data);
      }
    );
  }

  async get_available_audio_devices(): Promise<AvailableDevices> {
    return ipc_invoke(`get_available_audio_devices`, {}).then(
      (res: { data: AvailableDevices }) => res.data
    );
  }

  /**
   * Arms or disarms the native Windows device-change watcher (the only arm path). Idempotent;
   * resolves to whether it is armed after the call (always false off Windows).
   */
  async set_audio_device_watcher_enabled(enabled: boolean): Promise<boolean> {
    return ipc_invoke(`set_audio_device_watcher_enabled`, { enabled }).then(
      (res: { data: boolean }) => res.data
    );
  }

  async get_recording_state(): Promise<RecordingState> {
    return ipc_invoke(`get_recording_state`, {}).then((res: { data: RecordingState }) => {
      return res.data;
    });
  }

  /**
   * Liveness snapshot for zombie detection. Unlike get_recording_state it exposes
   * secs_since_last_data, so converge can force-stop a zombie without killing a live recording.
   */
  async get_recording_liveness(): Promise<RecordingLiveness> {
    return ipc_invoke(`get_recording_liveness`, {}).then(
      (res: { data: RecordingLiveness }) => res.data
    );
  }

  async get_recording_health(): Promise<WavWriterFailurePayload | null> {
    return ipc_invoke(`get_recording_health`, {}).then(
      (res: { data: WavWriterFailurePayload | null }) => res.data
    );
  }

  async get_files_for_session(
    sessionId: string
  ): Promise<{ name: string; bytes: number; size: number }[]> {
    return ipc_invoke(
      `get_audio_files_in_session`,
      { session_id: sessionId },
      { suppressErrorCodes: [AUDIO_SESSION_ID_INVALID_ERROR_CODE] }
    ).then((res: { data: { name: string; bytes: number; size: number }[] }) => {
      logger.debug(res);
      return res.data;
    });
  }
  async get_file_from_path(
    path: string,
    options: { deleteAfterRead?: boolean } = {}
  ): Promise<{ name: string; bytes: number; size: number }> {
    // Rust deletes the file once its bytes are read, for a plaintext copy nothing else needs.
    const params = options.deleteAfterRead ? { path, deleteAfterRead: true } : { path };
    return ipc_invoke(`get_audio_file_from_path`, params, {
      suppressErrorCodes: [AUDIO_PATH_OUTSIDE_MANAGED_DIR_ERROR_CODE],
    }).then((res: { data: { name: string; bytes: number; size: number } }) => {
      return res.data;
    });
  }

  /**
   * What the delete did. `failed` means the file may still be on disk. A path Rust refuses is logged
   * here and reads as `refused`. Other IPC errors reject.
   */
  async remove_file_from_path(path: string): Promise<RemoveFileOutcome> {
    try {
      const res: { data?: RemoveFileResult } = await ipc_invoke(
        `remove_recording_file`,
        { path },
        { suppressErrorCodes: [AUDIO_PATH_OUTSIDE_MANAGED_DIR_ERROR_CODE] }
      );
      return res.data?.outcome ?? 'failed';
    } catch (error) {
      if (bridgeErrorKind(error) !== AUDIO_PATH_OUTSIDE_MANAGED_DIR_ERROR_CODE)
        throw error;
      logger.warn('[recording] a file removal was refused', {
        errorKind: bridgeErrorKind(error),
      });
      return 'refused';
    }
  }

  /** Deletes a file and logs a delete that left it on disk, with no path. Never rejects. */
  async remove_file_quietly(path: string): Promise<void> {
    try {
      const outcome = await this.remove_file_from_path(path);
      if (outcome === 'failed') {
        logger.warn('[recording] a file the app asked to remove is still on disk');
      }
    } catch (error) {
      logger.warn('[recording] a file removal did not complete', {
        errorKind: bridgeErrorKind(error),
        error: sanitizeError(error),
      });
    }
  }

  /**
   * `should_use_smallest` is a size/recency heuristic for picking a WAV chunk off disk, used only
   * by the legacy chunk-upload loop. It must not feed note generation (`_actionPostRecording`).
   */
  async get_current_recording(): Promise<
    { name: string; bytes: number; size: number; path: string } | undefined
  > {
    return ipc_invoke(`get_current_recording`, {
      should_use_smallest: true,
    }).then((res: { data: string }) => {
      if (res.data) {
        const file = this.get_file_from_path(res.data).then((file) => {
          return {
            name: file.name,
            bytes: file.bytes,
            size: file.size,
            path: res.data,
          };
        });
        return file;
      }
    });
  }

  async inspect_audio_file(): Promise<
    { name: string; bytes: number; size: number; path: string } | undefined
  > {
    return ipc_invoke(`inspect_audio_file`, {}).then((res: { data: string }) => {
      if (res.data) {
        const file = this.get_file_from_path(res.data).then((file) => {
          return {
            name: file.name,
            bytes: file.bytes,
            size: file.size,
            path: res.data,
          };
        });
        return file;
      }
    });
  }

  async inspect_speaker_file(): Promise<
    { name: string; bytes: number; size: number; path: string } | undefined
  > {
    return ipc_invoke(`inspect_speaker_file`, {}).then((res: { data: string }) => {
      if (res.data) {
        const file = this.get_file_from_path(res.data).then((file) => {
          return {
            name: file.name,
            bytes: file.bytes,
            size: file.size,
            path: res.data,
          };
        });
        return file;
      }
    });
  }

  async screencapture_permission_granted_v2(): Promise<boolean> {
    return ipc_invoke(`screencapture_permission_granted_v2`, {}).then(
      (res: { data: boolean }) => res.data
    );
  }

  /**
   * **Prompts** when the user has never been asked, and collapses the status
   * into a bool. User-initiated paths only; everything else must use
   * {@link read_microphone_permission_status}. See {@link MicPermissionStatus}.
   */
  async request_microphone_permission(): Promise<boolean> {
    return ipc_invoke(`check_microphone_permission`, {}).then(
      (res: { data: boolean }) => res.data
    );
  }

  /** Reads the permission status without prompting. Safe from background paths. */
  async read_microphone_permission_status(): Promise<MicPermissionStatus> {
    return ipc_invoke(`read_microphone_permission_status`, {}).then(
      (res: { data: MicPermissionStatus }) => res.data
    );
  }

  async open_mic_privacy_settings(): Promise<void> {
    return ipc_invoke(`open_mic_privacy_settings`, {}).then(() => {});
  }

  /** OS-level mute / ~0% volume for the selected (or default) capture endpoint. */
  async check_input_device_health(
    deviceName?: string | null
  ): Promise<InputDeviceHealth> {
    return ipc_invoke(`check_input_device_health`, {
      deviceName: deviceName ?? null,
    }).then((res: { data: InputDeviceHealth }) => res.data);
  }

  /** Opens OS Sound settings. */
  async open_sound_settings(): Promise<void> {
    return ipc_invoke(`open_sound_settings`, {}).then(() => {});
  }

  async is_mac_os_app_in_applications(): Promise<boolean> {
    return ipc_invoke(`is_mac_os_app_in_applications`, {}).then(
      (res: { data: boolean }) => res.data
    );
  }

  async get_audio_file_directory_size(): Promise<number> {
    return ipc_invoke(`get_directory_size`, {}).then((res: { data: number }) => res.data);
  }

  async clear_audio_file_directory(): Promise<{ skipped: boolean }> {
    return ipc_invoke(`remove_directory`, {}).then(
      (res: { data: { skipped: boolean } }) => res.data
    );
  }

  async cleanup_recordings(): Promise<string> {
    return ipc_invoke(`cleanup_recordings`, {}).then((res: { data: string }) => res.data);
  }

  // -------------------------------------------------------------------------
  // Self-healing stop. Online transport only, gated by `useIsSelfHealingStopEnabled()`; web is a no-op.
  // -------------------------------------------------------------------------

  /**
   * The single idempotent terminal routine. Never discards not-yet-uploaded audio. Desktop-only.
   * A stale `(recordingId, generation)` is a no-op in Rust and resolves to `null`; other errors reject.
   */
  async stopAndFinalize(params: {
    recordingId: string;
    generation: number;
    reason: StopReason;
  }): Promise<TerminalOutcome | null> {
    if (!isTauri()) {
      throw new Error('stopAndFinalize is only available inside the Tauri runtime');
    }
    try {
      return await invokeSelfHealing<TerminalOutcome>('stop_and_finalize', params);
    } catch (error) {
      if (
        error instanceof SelfHealingBridgeError &&
        error.errorCode === SELF_HEALING_STALE_EPOCH_CODE
      ) {
        logger.debug('stopAndFinalize: stale epoch rejected (no-op)', params);
        return null;
      }
      throw error;
    }
  }

  /**
   * FE proves liveness for the active recording; the native watchdog observes staleness.
   * Resolves `false` for a stale lease (not the current native epoch) and on web, so the caller
   * should re-lease from `getRecordingStateSnapshot`.
   */
  async recordingLeaseHeartbeat(params: RecordingLeaseParams): Promise<boolean> {
    if (!isTauri()) return false;
    return ipc_invoke(`recording_lease_heartbeat`, params).then(
      (res: { data: boolean }) => res.data
    );
  }

  /**
   * Typed reconnect snapshot after a webview reload. On web there is no native
   * recording, so report an empty terminal snapshot rather than inferring a
   * "safely stopped" active recording from its absence.
   */
  async getRecordingStateSnapshot(): Promise<NativeStateSnapshot> {
    if (!isTauri()) {
      return {
        state: 'terminal',
        recordingId: '',
        generation: 0,
        terminalIntent: null,
        lastProgressAt: 0,
      };
    }
    return ipc_invoke(`get_recording_state_snapshot`, {}).then(
      (res: { data: NativeStateSnapshot }) => res.data
    );
  }

  /**
   * Same snapshot as {@link get_recording_liveness}, read without the destructive error toast. Rejects with
   * {@link SelfHealingBridgeError} for the caller to log; resolves `null` off-desktop ("nothing is recording"
   * vs "no native recorder"). `session_id` is only meaningful while `state` is `Recording` or `Paused`.
   */
  async getRecordingLivenessQuietly(): Promise<RecordingLiveness | null> {
    if (!isTauri()) return null;
    return invokeSelfHealing<RecordingLiveness>('get_recording_liveness', {});
  }

  /**
   * Reads the durable backup-recovery rows for the current user without the destructive error toast.
   * Callers are passive listeners, so rejections reach them to be logged. Empty on web. A read made
   * in answer to a click owns its own error UX, like {@link onlineBackupRetryNow}.
   */
  async onlineBackupRecoveryStatusQuietly(): Promise<BackupRecoveryRow[]> {
    if (!isTauri()) return [];
    return invokeSelfHealing<BackupRecoveryRow[]>('online_backup_recovery_status', {});
  }

  /**
   * Force a re-attempt of a specific pending backup (parity with an explicit
   * user retry; the automatic path is the boot reconcile). Desktop-only.
   */
  async onlineBackupRetryNow(params: {
    sessionId: string;
    recordingId: string;
  }): Promise<BackupRecoveryRow> {
    if (!isTauri()) {
      throw new Error('onlineBackupRetryNow is only available inside the Tauri runtime');
    }
    return ipc_invoke(`online_backup_retry_now`, params).then(
      (res: { data: BackupRecoveryRow }) => res.data
    );
  }

  /**
   * Tells Rust the FE uploaded this recording's backup itself, so its durable row stops being
   * retried or offered. Idempotent. Quiet on failure: the caller logs, no toast. Desktop-only.
   */
  async onlineBackupMarkUploaded(params: {
    sessionId: string;
    recordingId: string;
  }): Promise<BackupRecoveryRow> {
    if (!isTauri()) {
      throw new Error(
        'onlineBackupMarkUploaded is only available inside the Tauri runtime'
      );
    }
    return invokeSelfHealing<BackupRecoveryRow>('online_backup_mark_uploaded', params);
  }

  /**
   * Arms or disarms the background reconcile loop. Call only after the self-healing-stop flag
   * resolves and the user is authenticated. No-op on web. `payload` is the flag's raw Flagsmith
   * payload, decoded by Rust into the self-healing thresholds.
   */
  async onlineBackupArmReconcile(params: {
    enabled: boolean;
    payload?: Record<string, unknown>;
  }): Promise<void> {
    if (!isTauri()) return;
    // invokeSelfHealing (not ipc_invoke): arm/disarm must not raise a
    // destructive toast on a transient failure; the hook retries on remount.
    await invokeSelfHealing<null>('online_backup_arm_reconcile', params);
  }
}

export const recorderFmc = new RecordingModelController();
